import { API_BASE_URL } from '@/config'
import { translateBackendUnavailableReason } from '@/helper/apiError.helpers'
import { getSessionGeneration, onSessionReset, resetSessionScope } from '@/api/sessionScope'
import { useSystemStore } from '@/stores/system.store'

function isBackendDownStatus(status: number) {
  // consider 5xx as backend issues; you can narrow to 502/503/504 if you prefer
  return status >= 500
}

function markBackendUp() {
  useSystemStore.getState().setBackendUp()
}

function markBackendDown(reason?: string) {
  console.error('[API] Backend unavailable:', reason)
  useSystemStore.getState().setBackendDown(translateBackendUnavailableReason(reason))
}

let refreshPromise: Promise<boolean> | null = null

/**
 * The token refresh currently in flight, if any (SIB-924). The auth transition
 * flow awaits it (bounded) before sending the logout/login requests, so a
 * refresh response — including its Set-Cookie — is processed BEFORE the
 * transition's own responses in the normal completed-request flow.
 */
export function getPendingSessionRefresh(): Promise<boolean> | null {
  return refreshPromise
}

export type ApiRequestInit = RequestInit & {
  /**
   * A failing non-critical request (a preview, a hint, a badge count) must not flip the whole
   * app into the "Verbindung nicht verfügbar" overlay. Its errors are still thrown to the
   * caller; only the global backend-down marking is skipped for 5xx and network failures.
   */
  nonCritical?: boolean
}

// ---- GLOBAL GET DEDUPE + CACHE ----

type CacheEntry = { expiresAt: number; value: unknown }

// key: "GET /orders?year=2026"
const inFlightGET = new Map<string, Promise<unknown>>()
const cacheGET = new Map<string, CacheEntry>()

// The response cache and dedupe map hold user-dependent data but are keyed
// only by method + path, so they must never survive an authentication
// transition (SIB-924). The generation guard below additionally stops
// requests started in an old session from repopulating the maps afterwards.
onSessionReset(() => {
  cacheGET.clear()
  inFlightGET.clear()
  refreshPromise = null
})

const DEFAULT_GET_TTL_MS = 30_000 // tune (e.g. 30s)

function ttlFor(path: string) {
  if (path.startsWith('/orders?')) return 2 * 60_000 // 2 min
  if (path.startsWith('/settings/banks/')) return 10 * 60_000 // 10 min
  return DEFAULT_GET_TTL_MS
}

function canonicalizePath(path: string) {
  const [p, qs] = path.split('?')
  if (!qs) return path

  const sp = new URLSearchParams(qs)

  // sort keys + values
  const entries = Array.from(sp.entries()).sort(([aK, aV], [bK, bV]) => {
    if (aK !== bK) return aK.localeCompare(bK)
    return aV.localeCompare(bV)
  })

  const out = new URLSearchParams()
  for (const [k, v] of entries) out.append(k, v)

  const q = out.toString()
  return q ? `${p}?${q}` : p
}

function getMethod(init?: RequestInit) {
  return (init?.method || 'GET').toUpperCase()
}

function makeCacheKey(path: string, init?: RequestInit) {
  const method = getMethod(init)
  const canonicalPath = method === 'GET' ? canonicalizePath(path) : path
  return `${method} ${canonicalPath}`
}

function isCacheDisabled(init?: RequestInit) {
  const h = init?.headers as any
  return Boolean(h?.['x-disable-cache'] || h?.['X-Disable-Cache'])
}

export function invalidateApiCache(prefix?: string) {
  if (!prefix) {
    cacheGET.clear()
    return
  }
  for (const k of cacheGET.keys()) {
    if (k.includes(prefix)) cacheGET.delete(k)
  }
}

const CSRF_HEADER = 'X-CSRF-Token'
const CSRF_STORAGE_KEY = 'CSRF_TOKEN'

function storeCsrfFromResponse(res: Response) {
  const csrf = res.headers.get('x-csrf-token')
  if (csrf) {
    localStorage.setItem(CSRF_STORAGE_KEY, csrf)
  }
}

/**
 * The CSRF header for a state-changing request that bypasses `API` (for example a public
 * endpoint that still runs under the session cookie, such as logout). The backend rejects
 * such a request when the CSRF cookie is present but the header is missing.
 */
export function csrfHeaders(): Record<string, string> {
  return { [CSRF_HEADER]: localStorage.getItem(CSRF_STORAGE_KEY) || '' }
}

/**
 * A refresh failed or a retried request is still 401: the session is gone.
 * Reset the session scope so no user-dependent cache or store survives the
 * expiry (SIB-924), then hand the caller the established UNAUTHENTICATED error.
 * A SUCCESSFUL refresh never comes through here and keeps all caches.
 *
 * The reset only happens for the session the request belongs to: a delayed
 * failure from a request of a PREVIOUS session must not tear down the session
 * of the user who has logged in since.
 */
function failedSessionError(requestSessionGen: number) {
  if (requestSessionGen === getSessionGeneration()) {
    resetSessionScope()
  }
  return new Error('UNAUTHENTICATED')
}

/**
 * A 401 on a request whose session is already gone (logout/login happened
 * while it was in flight) is answered with a plain failure: no refresh is
 * attempted — the cookie now belongs to whoever is logged in, and retrying an
 * old request under the new session would hand its caller foreign data — and
 * nothing about the current session is touched.
 */
function isStaleRequest(requestSessionGen: number) {
  return requestSessionGen !== getSessionGeneration()
}

async function safeJson(res: Response) {
  try {
    return await res.json()
  } catch {
    return {}
  }
}
async function refreshSession(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...csrfHeaders(),
      },
    })

    storeCsrfFromResponse(res)

    if (res.ok || res.status < 500) {
      markBackendUp()
    } else if (isBackendDownStatus(res.status)) {
      markBackendDown(`HTTP ${res.status}`)
    }

    return res.ok
  } catch (e: any) {
    markBackendDown(e?.message || 'Network error')
    return false
  }
}

async function sendRequest(path: string, apiInit: ApiRequestInit): Promise<Response> {
  const { nonCritical, ...init } = apiInit

  const finalInit: RequestInit = {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeaders(),
      ...(init.headers || {}),
    },
  }

  try {
    const res = await fetch(`${API_BASE_URL}${path}`, finalInit)

    // If we reached backend and got any response => backend is reachable
    if (res.ok || res.status < 500) {
      markBackendUp()
    } else if (isBackendDownStatus(res.status) && !nonCritical) {
      markBackendDown(`HTTP ${res.status}`)
    }

    return res
  } catch (e: any) {
    // Network/CORS/DNS/connection => fetch throws
    if (!nonCritical) markBackendDown(e?.message || 'Network error')
    throw e
  }
}

export async function API<R>(
  path: string,
  init: ApiRequestInit = {},
  errorMsgFallback?: string,
  errorStatusFallback?: string
): Promise<R> {
  const method = getMethod(init)

  // ---- GET CACHE (fast return) ----
  if (method === 'GET' && !isCacheDisabled(init)) {
    const key = makeCacheKey(path, init)
    const hit = cacheGET.get(key)
    if (hit && hit.expiresAt > Date.now()) {
      return hit.value as R
    }

    // ---- GET IN-FLIGHT DEDUPE (share ongoing request) ----
    const existing = inFlightGET.get(key)
    if (existing) {
      return (await existing) as R
    }

    // Requests belong to the session they started in. If the session scope is
    // reset (logout, login, expiry) while this request is in flight, its
    // response must not be written into the next session's cache (SIB-924).
    const sessionGen = getSessionGeneration()
    const cacheResult = (data: unknown) => {
      if (sessionGen === getSessionGeneration()) {
        cacheGET.set(key, { value: data, expiresAt: Date.now() + DEFAULT_GET_TTL_MS })
      }
    }

    const p = (async () => {
      try {
        // run the ORIGINAL logic (copied from below)
        let res = await sendRequest(path, init)
        storeCsrfFromResponse(res)

        if (res.status !== 401) {
          const data = await handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
          cacheResult(data)
          return data
        }

        if (isStaleRequest(sessionGen)) {
          throw new Error('UNAUTHENTICATED')
        }

        console.warn('[API] 401 - attempting session refresh')

        if (!refreshPromise) {
          // The finally must only clear its OWN promise: after a session
          // transition this slot may already hold the next session's refresh,
          // and an old refresh completing must not break its dedupe.
          const startedRefresh: Promise<boolean> = refreshSession().finally(() => {
            if (refreshPromise === startedRefresh) {
              refreshPromise = null
            }
          })
          refreshPromise = startedRefresh
        }

        const refreshed = await refreshPromise

        if (!refreshed) {
          throw failedSessionError(sessionGen)
        }

        if (isStaleRequest(sessionGen)) {
          throw new Error('UNAUTHENTICATED')
        }

        console.warn('[API] Refresh OK, retrying original request…')
        res = await sendRequest(path, init)
        storeCsrfFromResponse(res)

        if (res.status === 401) {
          throw failedSessionError(sessionGen)
        }

        const data = await handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
        cacheResult(data)
        return data
      } finally {
        // Only remove our own entry: after a session reset the same key may
        // already hold the NEW session's in-flight request.
        if (inFlightGET.get(key) === p) {
          inFlightGET.delete(key)
        }
      }
    })()

    inFlightGET.set(key, p as Promise<unknown>)
    return (await p) as R
  }

  if (method !== 'GET') {
    cacheGET.clear() // simplest and safest
  }
  // ---- non-GET: keep original behavior (no caching/dedupe) ----
  const sessionGen = getSessionGeneration()
  let res = await sendRequest(path, init)

  storeCsrfFromResponse(res)

  if (res.status !== 401) {
    return handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
  }

  if (isStaleRequest(sessionGen)) {
    throw new Error('UNAUTHENTICATED')
  }

  console.warn('[API] 401 - attempting session refresh')

  if (!refreshPromise) {
    // See the GET path: only clear our own promise.
    const startedRefresh: Promise<boolean> = refreshSession().finally(() => {
      if (refreshPromise === startedRefresh) {
        refreshPromise = null
      }
    })
    refreshPromise = startedRefresh
  }

  const refreshed = await refreshPromise

  if (!refreshed) {
    throw failedSessionError(sessionGen)
  }

  if (isStaleRequest(sessionGen)) {
    throw new Error('UNAUTHENTICATED')
  }

  console.warn('[API] Refresh OK, retrying original request…')
  res = await sendRequest(path, init)

  storeCsrfFromResponse(res)

  if (res.status === 401) {
    throw failedSessionError(sessionGen)
  }

  return handleResponse<R>(res, errorMsgFallback, errorStatusFallback)
}
export async function API_PUBLIC<R>(
  path: string,
  init: RequestInit = {},
  errorMsg?: string,
  errorCode?: string
): Promise<R> {
  let res: Response

  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    })
  } catch (e: any) {
    markBackendDown(e?.message || 'Network error')
    throw e
  }

  storeCsrfFromResponse(res)

  if (res.ok || res.status < 500) {
    markBackendUp()
  } else if (isBackendDownStatus(res.status)) {
    markBackendDown(`HTTP ${res.status}`)
  }

  const data = await res.json().catch(() => ({}))

  if (!res.ok) {
    const err: any = new Error(data?.message || errorMsg)
    err.code = data?.code || errorCode
    err.errors = data?.errors
    err.data = data
    err.status = res.status
    throw err
  }

  return data
}

async function handleResponse<R>(
  res: Response,
  errorMsg?: string,
  errorCode?: string
): Promise<R> {
  const data = await safeJson(res)

  if (!res.ok) {
    const err: any = new Error(data?.message || errorMsg)
    err.code = data?.code || errorCode
    err.errors = data?.errors
    err.data = data
    err.status = res.status
    throw err
  }

  return data
}
export type BlobResponse = { blob: Blob; filename: string | null }

export async function API_BLOB(
  endpoint: string,
  options: RequestInit = {},
  errorMessage = 'Download fehlgeschlagen',
  errorCode?: string
): Promise<BlobResponse> {
  try {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/octet-stream',
        ...options.headers,
      },
      ...options,
    })

    if (res.ok || res.status < 500) {
      markBackendUp()
    } else if (isBackendDownStatus(res.status)) {
      markBackendDown(`HTTP ${res.status}`)
    }

    if (!res.ok) {
      const backendMessage = await res.text()
      throw { status: res.status, backendMessage, code: errorCode }
    }

    // Extract filename from Content-Disposition header
    const contentDisposition = res.headers.get('Content-Disposition')
    let filename: string | null = null
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
      if (match) {
        filename = match[1]
      }
    }

    const blob = await res.blob()
    return { blob, filename }
  } catch (e: any) {
    // if fetch threw (network)
    if (e?.name === 'TypeError' || !e?.status) {
      markBackendDown(e?.message || 'Network error')
    }
    console.error(errorCode || 'API_BLOB_ERROR', e)
    throw e
  }
}
